import { IFilterOption } from "@faire/web-api/indigofair/data/IFilterOption";
import { Language } from "@faire/web-api/indigofair/languages/Language";
import { ILocaleKey } from "@faire/web-api/indigofair/locale/ILocaleKey";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization";
import capitalize from "lodash/capitalize";

import {
  CATEGORY_KEY_PREFIX,
  ALL_PRODUCTS_KEY,
  NEW_ARRIVALS_KEY,
} from "@faire/retailer/components/LoggedOutBrand/consts";

export const getMetaTitle = (
  {
    brandCompanyName,
  }: {
    brandCompanyName: string | undefined;
  },
  strictLocalize: StrictLocalizeFunction
): string | null => {
  if (!brandCompanyName) {
    return null;
  }

  return strictLocalize(
    {
      defaultMessage: "{brandCompanyName} wholesale products",
      description: {
        text: "Meta-title for logged-out brand page (for SEO purposes)",
        img: "https://cdn.faire.com/fastly/cdd77466b1cf12590a8d9583ffc5865cbd72b416cd1503673fdba3ecf3fb211c.png",
      },
    },
    { brandCompanyName }
  );
};

export const getMetaDescription = (
  {
    brandCompanyName,
    categoryFilterOptions,
    fromCountry,
  }: {
    brandCompanyName: string | undefined;
    categoryFilterOptions: IFilterOption[] | null;
    fromCountry: string | undefined;
  },
  strictLocalize: StrictLocalizeFunction,
  localeKey: ILocaleKey | undefined
): string | null => {
  if (!brandCompanyName || !categoryFilterOptions?.length) {
    return null;
  }

  const filteredCategoryFilterOptions =
    filterNonCategoryNodesCategoryFilterOptions(categoryFilterOptions);

  if (filteredCategoryFilterOptions.length >= 3) {
    return strictLocalize(
      {
        defaultMessage:
          "{brandCompanyName} carries a selection of wholesale {firstCategory} products, {secondCategory} products, {thirdCategory} products, and more for your store.",
        description: {
          text: `Gives an overview of the brand (on their page) and the kinds of products they sell (used for search-engine optimization purposes to add more visibility on brand + category related searches).
            Note: translations should omit the word 'product' after each {category}: {brand.name} carries a selection of products in the categories {category 1} , {category 2} , {category 3}, and more for your store.`,
        },
      },
      DEPRECATED_backwardsCompatibleValues({
        brandCompanyName: brandCompanyName,
        firstCategory: getCategoryDisplayName(
          filteredCategoryFilterOptions[0],
          localeKey
        ),
        secondCategory: getCategoryDisplayName(
          filteredCategoryFilterOptions[1],
          localeKey
        ),
        thirdCategory: getCategoryDisplayName(
          filteredCategoryFilterOptions[2],
          localeKey
        ),
      })
    );
  } else if (filteredCategoryFilterOptions.length >= 1 && fromCountry) {
    return strictLocalize(
      {
        defaultMessage:
          "{brandCompanyName} specializes in wholesale {brandPrimaryCategory} products and are {FromCountry}.",
        description: {
          text: "Gives an overview of the brand (on their page) based on the primary category they sell as well as which country they're based in (used for search-engine optimization purposes to add more visibility on the brand).",
        },
      },
      DEPRECATED_backwardsCompatibleValues({
        brandCompanyName: brandCompanyName,
        brandPrimaryCategory: getCategoryDisplayName(
          filteredCategoryFilterOptions[0],
          localeKey
        ),
        FromCountry: fromCountry,
      })
    );
  }

  return null;
};

const capitalizeWhiteList: Set<Language> = new Set([
  Language.FRA,
  Language.DEU,
  Language.ITA,
  Language.SPA,
]);

const getCategoryDisplayName = (
  categoryFilter: IFilterOption | undefined,
  localeKey: ILocaleKey | undefined
): string => {
  const categoryDisplayName =
    categoryFilter?.display_name ?? categoryFilter?.global_display_name ?? "";

  const language = (localeKey?.language ??
    Language.LANGUAGE_UNKNOWN) as Language;
  if (capitalizeWhiteList.has(language)) {
    return capitalize(categoryDisplayName);
  }

  return categoryDisplayName.toLowerCase();
};

export const filterNonCategoryNodesCategoryFilterOptions = (
  categoryFilterOptions: IFilterOption[]
) => {
  const allProductsKey = `${CATEGORY_KEY_PREFIX}${ALL_PRODUCTS_KEY}`;
  const newArrivalsKey = `${CATEGORY_KEY_PREFIX}${NEW_ARRIVALS_KEY}`;

  const keysToRemove: Set<string> = new Set([allProductsKey, newArrivalsKey]);

  return categoryFilterOptions.filter(
    (category) => category.key && !keysToRemove.has(category.key)
  );
};
